import { Component, createSignal, For, Match, onMount, Show, Switch } from "solid-js"
import { createStore } from "solid-js/store";
import { cleanImagePath, fetchGet } from "../api";
import { PageList } from "../components/page-list";
import { Artwork } from "../types";
import mediumZoom from "medium-zoom";
import 'medium-zoom/dist/style.css';


type Props = {
    artwork: Artwork;
}
type TimelineEvent = {
    Id: string;
    Name: string;
    Description: string;
    EventType: string;
    StartCalendar: string;
    StartEpoch: string;
    StartProximity: string;
    StartYear: string;
    StartMonth: string;
    StartDay: string;
    EndCalendar: string;
    EndEpoch: string;
    EndProximity: string;
    EndYear: string;
    EndMonth: string;
    EndDay: string;
    DateAddedUTC: string;
    DateModifiedUTC: string;
    AutoGenerated: boolean;
    IsDeleted: boolean;
    ParentId: string;
    AddressId: string;
    Artist?: any;
    Artwork?: any;
}
type Filter = {
    ArtistId: string;
    FromYear: string;
    ToYear: string;
    Page: number;
};
type Search ={
    Filter: Filter;
    Count: number;
    Page: number;
    MaxPage: number;
    PageRows: number;
    Results: TimelineEvent[];
}
type Resources = {
    Pages: number[]
}
type CalendarDateProps = {
    year: string;
    month: string;
    day: string;
}
const months = ["January","February","March","April","May","June","July","August","September", "October", "November", "December"];
export const CalendarDate : Component<CalendarDateProps> = ({ year, month, day }) => {
    return (
        <>
            <Show when={year}>
                <Switch>
                    <Match when={month && !day}>
                        <span style="font-size: 1rem;">{months[parseInt(month) - 1]}</span>
                    </Match>
                    <Match when={month && day}>
                        <span style="font-size: 1rem;">{months[parseInt(month) - 1]} {day}</span>
                    </Match>
                </Switch>
                <div style="font-size: 2rem;">
                    {year}
                </div>
            </Show>
        </>
    )
}

export const TimelineTab : Component<Props> = (props) => {
    const [search, setSearch] = createStore<Search>({
        Filter: {
            FromYear: "",
            ToYear: ""
        }
    } as Search);
    const [resources, setResources] = createStore<Resources>({} as Resources);
    const [page,setPage] = createSignal<number>(1);
    const [maxPage,setMaxPage] = createSignal<number>(1);
    const [blocksize,setBlocksize] = createSignal<number>(20);
    const loadEvents = async () => {
        const res = await fetchGet("/Artist/SearchTimelineEventsJson", {
            ArtistId: search.Filter.ArtistId || "",
            FromYear: search.Filter.FromYear || "",
            ToYear: search.Filter.ToYear || "",
            Page: page()
        });
        const json = await res.json();
        if (!json.Success){
            return;
        }
        setPage(json.Data.Page);
        setMaxPage(json.Data.MaxPage);
        setSearch(json.Data);
        setResources("Pages", generatePages());
        mediumZoom(".zoomable");
    };
    const generatePages = () => {
        const list = [];
        for (let p = 0; p < search.MaxPage; p++) {
            list.push(p);
        }
        return list;
    }
    const updatePageNumber = async (p: number) => {
        setPage(p);
        await loadEvents();
    }
    const presetFilter = async () => {
        if (props.artwork.startYear) {
            setSearch("Filter", "FromYear", props.artwork.startYear);
            setSearch("Filter", "ToYear", props.artwork.endYear > "0" ? props.artwork.endYear : props.artwork.startYear);
            return;
        }
        setSearch("Filter", "ArtistId", props.artwork.artist.id + "");
        const res = await fetchGet("/Artist/GetTimelineEventRangesJson", { id: props.artwork.artist.id });
        const json = await res.json();
        if (!json.Success) return;
        setSearch("Filter", "FromYear", json.Data.FromYear);
        setSearch("Filter", "ToYear", json.Data.ToYear || json.Data.FromYear);
    }
    type State = {
        editFromYear: boolean;
        editToYear: boolean;
        filterByArtist: boolean;
    }
    const toggleRestrictToArtist = async () => {
        setState("filterByArtist", !state.filterByArtist);
        setSearch("Filter", "ArtistId", state.filterByArtist ? props.artwork.artist.id + "" : "");
        setSearch("Filter", "Page", 1);
        setPage(1);
        setMaxPage(1);
        await loadEvents();
    }
    const [state, setState] = createStore<State>({
        filterByArtist: !props.artwork.startYear
    } as State);
    onMount(async () => {
        await presetFilter();
        await loadEvents();
    });
    return (
        <>
            <div class="container">
                <div class="timeline-container">
                    <div>
                        <input
                            type="checkbox"
                            checked={state.filterByArtist}
                            onInput={toggleRestrictToArtist}
                        /> Show only events concerning {props.artwork.artist.firstName} {props.artwork.artist.lastName}
                    </div>
                    <h4>
                        {search?.Count?.toLocaleString()} events between 
                        <span>
                            <Show when={state.editFromYear}>
                                <input
                                    type="text"
                                    class="form-control timeline-year-input mr-5"
                                    value={search.Filter.FromYear}
                                    onChange={async (e) => { setState("editFromYear", false); setSearch("Filter", "FromYear", e.currentTarget.value); await loadEvents(); }} />
                            </Show>
                            <Show when={!state.editFromYear}>
                                <span class="timeline-year-label" style="margin-right: 5px;" onClick={() => setState("editFromYear", true)}>
                                    {search.Filter.FromYear}        
                                </span>
                            </Show>
                        </span>
                        and
                        <span>
                            <Show when={state.editToYear}>
                                <input
                                    type="text"
                                    class="form-control timeline-year-input ml-5"
                                    value={search.Filter.ToYear}
                                    onChange={async (e) =>  { setState("editToYear", false); setSearch("Filter", "ToYear", e.currentTarget.value); await loadEvents(); }} />
                            </Show>
                            <Show when={!state.editToYear}>
                                <span class="timeline-year-label" style="margin-left: 5px;" onClick={() => setState("editToYear", true)}>
                                    {search.Filter.ToYear}        
                                </span>
                            </Show>
                        </span>
                    </h4>
                    <div>
                        Page {page()?.toLocaleString()} of {maxPage()?.toLocaleString()}
                    </div>
                    <div class="pagelist-container">
                        <PageList page={page} maxPage={maxPage} blockSize={blocksize} onClick={(p) => updatePageNumber(p)} />
                    </div>
                    <div class="scroll-container">
                        <table class="table table-condensed">
                            <thead>
                                <tr>
                                    <th class="image">

                                    </th>
                                    <th class="dates">
                                        
                                    </th>
                                    <th>
                                        
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={search.Results}>{(event) =>
                                    {
                                        return <tr classList={{ ["event-highlight"]: search.Filter.ArtistId == "" && event.Artist?.Id == props.artwork.artist.id }}>
                                            <td class="image">
                                                <Show when={event.Artist && !event.Artwork && event.Artist.PrimaryImage}>
                                                    <img
                                                        style={`max-height: 125px; max-width: 125px;`}
                                                        src={cleanImagePath(event.Artist.PrimaryImage.URL) + "?mode=crop&format=JPEG&maxWidth=650&maxHeight=650"}
                                                        class="zoomable"
                                                    />
                                                </Show>
                                                <Show when={event.Artwork && event.Artwork.PrimaryImage}>
                                                    <img
                                                        style={`max-height: 125px; max-width: 125px;`}
                                                        src={cleanImagePath(event.Artwork.PrimaryImage.URL) + "?mode=crop&format=JPEG&maxWidth=650&maxHeight=650"}
                                                        class="zoomable"
                                                    />
                                                </Show>
                                            </td>
                                            <td class="dates">
                                                <CalendarDate year={event.StartYear} month={event.StartMonth} day={event.StartDay} />
                                                <Show when={event.EndYear}>
                                                    <div>to</div>
                                                    <CalendarDate year={event.EndYear} month={event.EndMonth} day={event.EndDay} />
                                                </Show>
                                            </td>
                                            <td class="description">
                                                <div innerHTML={event.Description} style="font-size: 1rem;" />
                                            </td>
                                        </tr>
                                    }
                                }</For>
                            </tbody>
                        </table>
                    </div>
                    <div class="pagelist-container">
                        <PageList page={page} maxPage={maxPage} blockSize={blocksize} onClick={(p) => updatePageNumber(p)} />
                    </div>
                </div>
            </div>
        </>
    )
}